import {Component, OnInit, ChangeDetectorRef, ViewChild, HostListener, forwardRef, Input, OnChanges, SimpleChanges, ElementRef, AfterViewInit} from '@angular/core';
import {v4 as uuidv4} from 'uuid';
import { FormGroup, NG_VALUE_ACCESSOR, NG_VALIDATORS, ControlValueAccessor, FormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute } from '@angular/router';
import { FlatTreeControl } from '@angular/cdk/tree';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import { PageBuilderComponentsService } from './page-builder-components.service'
import { PAGE_BUILDER_CODE_COMPONENT, PageBuilderItem } from './page-builder-components.models'

@Component({
    selector: 'abw-page-builder',
    providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => PageBuilderComponent),
        multi: true
      },
      {
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => PageBuilderComponent),
        multi: true
      }
    ],
    templateUrl: './page-builder.component.html',
    styleUrls: ['./page-builder.component.scss']
})
export class PageBuilderComponent implements OnInit, OnChanges, AfterViewInit, ControlValueAccessor {
    @Input() config: { ITEMS_DATASOURCES: any, SECTIONS_DATASOURCES: [] };
    @Input() sectionsDatasources: any[] = [];
    value: any;

    PAGE_BUILDER_CODE_COMPONENT = PAGE_BUILDER_CODE_COMPONENT;
    toolboxComponents: PageBuilderItem[] = [];
    selectedComponent: any;
    selectedColumn: any;
    propertiesFormGroup: FormGroup;
    propertiesFormModel: any;
    posDesignerItems: PageBuilderItem[] = [
        { 
            INDEX: 1,
            ID: uuidv4(),
            CODE: PAGE_BUILDER_CODE_COMPONENT.ROW, 
            ITEMS: [
                { CODE: PAGE_BUILDER_CODE_COMPONENT.COLUMN, INDEX: 1, ID: uuidv4(), ORIENTATION: 'COLUMN', ALIGNMENT: 'CENTER' }
            ]
        },
    ];
    formComponentsModels = {};

    @ViewChild(MatMenuTrigger, { static: true }) matMenuTrigger: MatMenuTrigger;
    menuTopLeftPosition = { x: '0', y: '0' };
    mousePosition = { x: 0, y: 0 };
    @HostListener('mousemove', ['$event']) onMouseMove(event) {
        this.mousePosition.x = event.clientX;
        this.mousePosition.y = event.clientY;
    }
    @ViewChild('pbDesigner') pbDesignerRef: ElementRef;

    constructor(
        private pageBuilderComponentsService: PageBuilderComponentsService,
        private route: ActivatedRoute,
        private cdf: ChangeDetectorRef,
    ) {
        this.toolboxComponents = pageBuilderComponentsService.doGetPageBuilderComponents()
        .filter(c => [
            PAGE_BUILDER_CODE_COMPONENT.TEXTBOX, 
            PAGE_BUILDER_CODE_COMPONENT.NUMERIC_TEXTBOX, 
            PAGE_BUILDER_CODE_COMPONENT.COLUMN, 
            PAGE_BUILDER_CODE_COMPONENT.ROW
        ].lastIndexOf(c.CODE) == -1);
    }

    ngOnInit() {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!!changes.config?.currentValue) {
            this.dataSource.data = this.config.ITEMS_DATASOURCES;
        }
    }

    ngAfterViewInit() {
    }

    propagateChange = (_: any) => {};
    registerOnChange(fn) { this.propagateChange = fn; }
    registerOnTouched() {}
    writeValue(value: any) {
        this.posDesignerItems = this.buildPageBuilderComponents(value);
        this.value = value; 
    }
	validate({ value }: FormControl) {
		return true;
	}

    private buildPageBuilderComponents(items: any) {
        if (!items) { return; }
        items.forEach(item => {
            if (!!item.ITEMS) { 
                this.buildPageBuilderComponents(item.ITEMS); 
            } else {
                const formModel = this.pageBuilderComponentsService.doBuildPageBuilderModel([item])[0];
                if (!!formModel) {
                    const formGroup = this.pageBuilderComponentsService.doBuildPageBuilderFormGroup([formModel]);
                    this.formComponentsModels[item.ID] = { FORM_MODEL: formModel, FORM_GROUP: formGroup };
                }
            }
        });
        return items;
    }
    
    onRightClick(event) {
          // preventDefault avoids to show the visualization of the right-click menu of the browser 
          event.preventDefault();

          this.click(this.mousePosition.x, this.mousePosition.y);
    
          // we record the mouse position in our object 
          this.menuTopLeftPosition.x = event.clientX + 'px';
          this.menuTopLeftPosition.y = (event.clientY - 50) + 'px';
          // we open the menu 
          this.matMenuTrigger.openMenu();
    }

    addRow(cols: number) {
        this.posDesignerItems.push({ 
            INDEX: this.posDesignerItems.length, 
            ID: uuidv4(), 
            CODE: PAGE_BUILDER_CODE_COMPONENT.ROW,
            ITEMS: Array.from({length: cols}, (v, k) => ({ CODE: PAGE_BUILDER_CODE_COMPONENT.COLUMN, INDEX: 0, ID: uuidv4(), }))
        });
    }

    deselectAll(items: PageBuilderItem[], except?: PageBuilderItem) {
        items.forEach(item => {
            if (!!except) {
                if (!this.hasChild(item, except)) {
                    item.SELECTED = false;
                } else {
                    item.SELECTED = true;
                }
            } else {
                item.SELECTED = false;
            }
            if (!!item.ITEMS) { this.deselectAll(item.ITEMS, except); }
        });
        if (!!this.selectedComponent) { this.selectedComponent = null; }
        if (!!this.selectedColumn) { this.selectedColumn = null; }
    }

    selectToolboxComponent(component: any) {
        if (!this.selectedColumn) { return; }
        this.selectedComponent = component;

        const propertyIndex = this.selectedComponent.PROPERTIES.findIndex(p => p.ID == 'INDEX');
        if (propertyIndex > -1) {
            this.selectedComponent.PROPERTIES[propertyIndex].DEFAULT_VALUE = !!this.selectedColumn.ITEMS ? this.selectedColumn.ITEMS.length + 1 : 1;
        }

        this.buildPropertiesFormGroup(this.selectedComponent);
    }

    selectPreviewItem(item: PageBuilderItem) {
        this.deselectAll(this.posDesignerItems, item);
        item.SELECTED = true;

        const basedComponent = this.pageBuilderComponentsService.doGetPageBuilderComponents().find(c => c.CODE == item.CODE);
        if (!!basedComponent) {
            const clonedComponent = JSON.parse(JSON.stringify(basedComponent));
            Object.keys(item).forEach(k => {
                const propertyIndex = clonedComponent.PROPERTIES.findIndex(p => p.ID == k);
                if (propertyIndex > -1) {
                    clonedComponent.PROPERTIES[propertyIndex].DEFAULT_VALUE = item[k];
                } else {
                    clonedComponent[k] = item[k];
                }
            });
            if (item.CODE == PAGE_BUILDER_CODE_COMPONENT.ROW) {
                const bindingPropertyIndex = clonedComponent.PROPERTIES.findIndex(p => p.ID == 'BINDING');
                if (bindingPropertyIndex > -1) {
                    clonedComponent.PROPERTIES[bindingPropertyIndex].DS_DATAS = !!this.config?.SECTIONS_DATASOURCES ? this.config.SECTIONS_DATASOURCES : [];
                }
            }
            this.selectedComponent = clonedComponent;
            this.buildPropertiesFormGroup(clonedComponent);
        }

        if (item.CODE == PAGE_BUILDER_CODE_COMPONENT.COLUMN) {
            this.selectedColumn = item;
        }
    }

    private hasChild(parent: PageBuilderItem, child: PageBuilderItem) {
        if (!parent.ITEMS) { return false; }
        let result = false;
        parent.ITEMS.forEach(item => {
            if (item.ID == child.ID) { result = true; }
            else if (!!item.ITEMS) {
                result = this.hasChild(item, child);
            }
        }); 

        return result;
    }

    private buildPropertiesFormGroup(item: PageBuilderItem) { 
        this.propertiesFormModel = this.pageBuilderComponentsService.doBuildPageBuilderModel(item.PROPERTIES);
        this.propertiesFormGroup = this.pageBuilderComponentsService.doBuildPageBuilderFormGroup(this.propertiesFormModel);
        this.cdf.detectChanges();
    }

    onComponentSaved() {
        if (!this.selectedComponent) { return; }

        const wrapperDivElement = this.pbDesignerRef.nativeElement;
        const componentDivElement = document.getElementById('page-builder-item-' + this.selectedComponent.ID);
        Object.keys(this.propertiesFormGroup.controls).forEach(k => {
            const propIndex = this.selectedComponent.PROPERTIES.findIndex(p => p.ID == k);
            if (propIndex > -1) {
                this.selectedComponent.PROPERTIES[propIndex].DEFAULT_VALUE = this.propertiesFormGroup.controls[k].value;
            }
        });
        const itemProperties: any = {
            INDEX: this.selectedComponent.INDEX, 
            ID: this.selectedComponent.ID, 
            CODE: this.selectedComponent.CODE,
            DEFAULT_VALUE: this.selectedComponent.DEFAULT_VALUE,
            BINDING: this.selectedComponent.BINDING,
            ITEMS: this.selectedComponent.ITEMS,
            POS_X: componentDivElement.offsetLeft - wrapperDivElement.offsetLeft,
            POS_Y: componentDivElement.offsetTop - wrapperDivElement.offsetTop,
            WIDTH: componentDivElement.clientWidth,
            HEIGHT: componentDivElement.clientHeight
        };
        this.selectedComponent.PROPERTIES.forEach(p => {
            if (itemProperties[p.ID] == undefined && !!p.DEFAULT_VALUE) { itemProperties[p.ID] = p.DEFAULT_VALUE; }
        });
        if ([
            PAGE_BUILDER_CODE_COMPONENT.COLUMN, 
            PAGE_BUILDER_CODE_COMPONENT.ROW
        ].lastIndexOf(this.selectedComponent.CODE) == -1) {
            const formModel = this.pageBuilderComponentsService.doBuildPageBuilderModel([itemProperties])[0];
            const formGroup = this.pageBuilderComponentsService.doBuildPageBuilderFormGroup([formModel]);
            this.formComponentsModels[this.selectedComponent.ID] = { FORM_MODEL: formModel, FORM_GROUP: formGroup };
        }

        if ([PAGE_BUILDER_CODE_COMPONENT.ROW].lastIndexOf(this.selectedComponent.CODE) > -1) {
            const rowIndex = this.posDesignerItems.findIndex(i => i.ID == this.selectedComponent.ID);
            if (rowIndex > -1) {
                this.posDesignerItems[rowIndex] = itemProperties;
                this.posDesignerItems = this.posDesignerItems.sort((a, b) => {
                    if (a.INDEX !== undefined && b.INDEX !== undefined) {
                        const index1 = parseInt(a.INDEX.toString());
                        const index2 = parseInt(b.INDEX.toString());
                        return index1 != index2 ? index1 - index2 : -1;
                    }
                    return -1;
                });
                this.value = JSON.parse(JSON.stringify(this.posDesignerItems));
                this.propagateChange(this.value);
            }
        } else {
            for (var i = 0; i < this.posDesignerItems.length; i++) {
                let path = this.findIndexNested(this.posDesignerItems[i], this.selectedComponent.ID);
                if (!!path) {
                    this.replacebyPath(this.posDesignerItems[i], path, 0, path.length, itemProperties);
                    break;
                }
            }
        }
        
        this.value = JSON.parse(JSON.stringify(this.posDesignerItems));
        this.propagateChange(this.value);
    }

    keepOrder = (a, b) => { return a; }

    private click(x,y){
        var ev = document.createEvent("MouseEvent");
        var el = document.elementFromPoint(x,y);
        ev.initMouseEvent(
            "click",
            true /* bubble */, true /* cancelable */,
            window, null,
            x, y, 0, 0, /* coordinates */
            false, false, false, false, /* modifier keys */
            0 /*left*/, null
        );
        el.dispatchEvent(ev);
    }

    onToolboxComponentDragStart(component: any, event: any) {
        const obj = { TYPE_ITEM: 1, ITEM: component };
        event.dataTransfer.clearData();
        event.dataTransfer.setData("PAGE_BUILDER_ITEM_DRAGGED", JSON.stringify(obj));
    }

    onDatasourceItemDragStart(dsItem: any, event: any) {
        const pageBuilderModel = { CODE: dsItem.CODE, DEFAULT_VALUE: dsItem.DEFAULT_VALUE, BINDING: dsItem.BINDING };
        const obj = { TYPE_ITEM: 2, ITEM: pageBuilderModel };
        event.dataTransfer.clearData();
        event.dataTransfer.setData("PAGE_BUILDER_ITEM_DRAGGED", JSON.stringify(obj));
    }

    onDragOver(e) {
        e.dataTransfer.dropEffect = "move";
        event.preventDefault();
    }
    onDragLeave(e) {
        e.preventDefault();
    }

    onDrop(designerItem: any, event: any) {
        try {
            if (!designerItem.ITEMS) { designerItem.ITEMS = []; }
            const dragObj: { TYPE_ITEM: number, ITEM: any } = JSON.parse(event.dataTransfer.getData("PAGE_BUILDER_ITEM_DRAGGED"));
            if (dragObj.TYPE_ITEM == 1) {
                const itemProperties: any = {
                    INDEX: designerItem.ITEMS.length + 1, 
                    ID: uuidv4(), 
                    CODE: dragObj.ITEM.CODE,
                };
                dragObj.ITEM.PROPERTIES.forEach(property => {
                    if (property.DEFAULT_VALUE !== undefined) {
                        itemProperties[property.ID] = property.DEFAULT_VALUE;
                    }
                });
                const formModel = this.pageBuilderComponentsService.doBuildPageBuilderModel([itemProperties])[0];
                const formGroup = this.pageBuilderComponentsService.doBuildPageBuilderFormGroup([formModel]);
                this.formComponentsModels[itemProperties.ID] = { FORM_MODEL: formModel, FORM_GROUP: formGroup };
                
                designerItem.ITEMS.push(itemProperties);
                this.selectPreviewItem(itemProperties);
            } else if (dragObj.TYPE_ITEM == 2) {
                const itemProperties: any = {
                    INDEX: designerItem.ITEMS.length + 1, 
                    ID: uuidv4(), 
                    CODE: dragObj.ITEM.CODE,
                    DEFAULT_VALUE: dragObj.ITEM.DEFAULT_VALUE,
                    BINDING: dragObj.ITEM.BINDING
                };
                const formModel = this.pageBuilderComponentsService.doBuildPageBuilderModel([itemProperties])[0];
                const formGroup = this.pageBuilderComponentsService.doBuildPageBuilderFormGroup([formModel]);
                this.formComponentsModels[itemProperties.ID] = { FORM_MODEL: formModel, FORM_GROUP: formGroup };

                designerItem.ITEMS.push(itemProperties);
                this.selectPreviewItem(itemProperties);
            }
        } catch(e) {}
    }

    private findIndexNested(data, id) {
        if (data.ID === id) return [];
        let result;
        const i = (data.ITEMS || []).findIndex(child => {
            return result = this.findIndexNested(child, id)
        });
        if (result) return [i, ...result];
    }

    private replacebyPath(obj, path, i, len, data ) {
        if(len===i+1){  
            obj.ITEMS[path[i]] = data;
            obj.ITEMS = obj.ITEMS.sort((a, b) => {
                if (!!a.INDEX && !!b.INDEX) {
                    const index1 = parseInt(a.INDEX.toString());
                    const index2 = parseInt(b.INDEX.toString());
                    return index1 != index2 ? index1 - index2 : -1;
                }
                return -1;
            });
            obj.ITEMS.forEach((a, i) => a.INDEX = i + 1);
            return obj;
        }
        if (!!obj && !!obj.ITEMS) {
            obj = this.replacebyPath(obj.ITEMS[path[i]],path, i+1, len, data );  
        }
        return obj;
    }

    private removebyPath(obj, path, i, len ) {
        if(len===i+1){  
            obj.ITEMS.splice(path[i], 1);
            return;
        }
        if (!!obj && !!obj.ITEMS) {
            this.removebyPath(obj.ITEMS[path[i]],path, i+1, len );  
        } 
    }

    onMenuItemClicked(itemId: string) {
        if (itemId == 'DELETE_ROW') {
            const rowIndex = this.posDesignerItems.findIndex(i => i.ID == this.selectedComponent.ID);
            if (rowIndex > -1) {
                this.posDesignerItems.splice(rowIndex, 1);
                this.value = JSON.parse(JSON.stringify(this.posDesignerItems));
                this.propagateChange(this.value);
            }
        } else if (itemId == 'ADD_COLUMN') {
            const rowIndex = this.posDesignerItems.findIndex(i => i.ID == this.selectedComponent.ID);
            if (rowIndex > -1) {
                this.posDesignerItems[rowIndex].ITEMS.push({
                    CODE: PAGE_BUILDER_CODE_COMPONENT.COLUMN,
                    ID: uuidv4(),
                    INDEX: this.posDesignerItems[rowIndex].ITEMS.length + 1,
                    ITEMS: []
                });
                this.value = JSON.parse(JSON.stringify(this.posDesignerItems));
                this.propagateChange(this.value);
            }
        } else if (itemId == 'DELETE_ITEM') {
            for (var i = 0; i < this.posDesignerItems.length; i++) {
                let path = this.findIndexNested(this.posDesignerItems[i], this.selectedComponent.ID);
                if (!!path) {
                    this.removebyPath(this.posDesignerItems[i], path, 0, path.length);
                    this.deselectAll(this.posDesignerItems);
                    this.propertiesFormGroup = null;
                    this.propertiesFormModel = null;
                    break;
                }

                if (!!this.formComponentsModels[this.selectedComponent.ID]) { delete this.formComponentsModels[this.selectedComponent.ID]; }
            }
        } else if (itemId == 'MOVE_UP_ROW') {
            let path = this.findInnerComponentIndex(this.posDesignerItems, this.selectedComponent.ID);
            if (!!path) {
                this.moveInnerComponent(this.posDesignerItems, [...path], 'UP');

                this.deselectAll(this.posDesignerItems);
                this.propertiesFormGroup = null;
                this.propertiesFormModel = null;
                
                path.splice(path.length - 1, 1);
                this.reorderInnerComponents(this.posDesignerItems, [...path]);

                this.value = JSON.parse(JSON.stringify(this.posDesignerItems));
                this.propagateChange(this.value);
            }
        } else if (itemId == 'MOVE_DOWN_ROW') {
            let path = this.findInnerComponentIndex(this.posDesignerItems, this.selectedComponent.ID);
            if (!!path) {
                this.moveInnerComponent(this.posDesignerItems, [...path], 'DOWN');

                this.deselectAll(this.posDesignerItems);
                this.propertiesFormGroup = null;
                this.propertiesFormModel = null;
                
                path.splice(path.length - 1, 1);
                this.reorderInnerComponents(this.posDesignerItems, [...path]);

                this.value = JSON.parse(JSON.stringify(this.posDesignerItems));
                this.propagateChange(this.value);
            }
        } else if (itemId == 'ADD_ROW_BELOW') {
            const rowIndex = this.posDesignerItems.findIndex(i => i.ID == this.selectedComponent.ID);
            if (rowIndex > -1 && rowIndex + 1 < this.posDesignerItems.length) {
                this.posDesignerItems.splice(rowIndex + 1, 0 , { 
                    INDEX: this.posDesignerItems.length, 
                    ID: uuidv4(), 
                    CODE: PAGE_BUILDER_CODE_COMPONENT.ROW,
                    ITEMS: Array.from({length: 1}, (v, k) => ({ CODE: PAGE_BUILDER_CODE_COMPONENT.COLUMN, INDEX: 0, ID: uuidv4(), }))
                });
            } else {
                this.posDesignerItems.push({ 
                    INDEX: this.posDesignerItems.length, 
                    ID: uuidv4(), 
                    CODE: PAGE_BUILDER_CODE_COMPONENT.ROW,
                    ITEMS: Array.from({length: 1}, (v, k) => ({ CODE: PAGE_BUILDER_CODE_COMPONENT.COLUMN, INDEX: 0, ID: uuidv4(), }))
                });
            }
        }
    }
    
    private findInnerComponentIndex(components: any[], searchValue: any) {
        for (var i = 0; i < components.length; i++) {
            let path = this.findIndexNested(components[i], searchValue);
            if (!!path) {
                return [i, ...path];
            } else if (!!components[i].ITEMS) {
                path = this.findInnerComponentIndex(components[i].ITEMS, searchValue);
                if (!!path) {
                    return [i, ...path];
                }
            }
        }
        return undefined;
    }

    private moveInnerComponent(components: any[], path: any[], direction: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT') {
        path.forEach((p, i ,arr) => {
            if (i + 1 < arr.length) {
                this.moveInnerComponent(components[p].ITEMS, arr.splice(1), direction);
            } else {
                if ((direction == 'UP' || direction == 'LEFT') && p > 0) {
                    const tmp = JSON.parse(JSON.stringify(components[p - 1]));
                    components[p - 1] = JSON.parse(JSON.stringify(components[p]));
                    components[p] = tmp;
                } else if ((direction == 'DOWN' || direction == 'RIGHT') && p + 1 < components.length) {
                    const tmp = JSON.parse(JSON.stringify(components[p + 1]));
                    components[p + 1] = JSON.parse(JSON.stringify(components[p]));
                    components[p] = tmp;
                }
            }
        });
    }

    private reorderInnerComponents(components: any[], path: any[]) {
        path.forEach((p, i , arr) => {
            if (i + 1 < arr.length) {
                this.reorderInnerComponents(components[p].ITEMS, path.splice(1));
            } else {
                (components[p].ITEMS as any[]).forEach((item, index, items) => { 
                    items[index].INDEX = index + 1; 
                });
            }
        });
    }

    private _transformer = (node: any, level: number) => {
        return Object.assign({
            expandable: !!node.children && node.children.length > 0,
            level: level,
            name: node.LABEL,
        }, node);
    }
    
    treeControl = new FlatTreeControl<any>(node => node.level, node => node.expandable);
    treeFlattener = new MatTreeFlattener(this._transformer, node => node.level, node => node.expandable, node => node.children);
    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    hasTreeChild = (_: number, node: any) => node.expandable;
    
    doExportTemplateAction() {
        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(this.value));
        const link = document.createElement("a");
        link.setAttribute("href", dataStr);
        link.setAttribute("download", `template.apb`);
        document.body.appendChild(link); // Required for FF
        link.click();
    }

    doImportTemplateAction() {
        const inputFile = document.createElement("input");
        inputFile.type = 'file';
        inputFile.accept = '.apb';
        inputFile.onchange = () => {
            inputFile.files[0].arrayBuffer().then((arrayBuffer) => {
                const rawContentFile = new TextDecoder().decode(arrayBuffer);
                const contentFile = JSON.parse(rawContentFile);
                this.posDesignerItems = this.buildPageBuilderComponents(contentFile);
                this.value = contentFile; 
                this.propagateChange(this.value);
            });
        };
        inputFile.click();
    }

    doResetTemplateAction() {
        this.posDesignerItems = this.buildPageBuilderComponents([]);
        this.value = []; 
        this.propagateChange(this.value);
    }
}